import React, {useCallback, useState} from 'react';
import {DragStatus} from '@store/call-card/CallCard.types';
import {SetState} from '@store/@types/util/React';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {CommentIcon} from '@components/icon/CommentIcon';
import {withStyles} from '@material-ui/core/styles';
import {UserComment} from 'records/UserComment';
import {Seconds} from '@store/@types/util';
import {UserId} from 'records/User';
import {ShareIcon} from '@components/icon/ShareIcon';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import SharingFormActions from 'actions/sharing/SharingFormActions';
import SharingActions from 'actions/sharing/SharingActions';
import {SpinnerCircular} from 'spinners-react';
import CallCardActions from '@store/call-card/CallCardActions';
import Dialog from '@material-ui/core/Dialog';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import './TrackHighlight.sass';
import CommentSource from '@store/call-card/CommentSource';

const DEFAULT_SHARE = 'Shared call segment';

interface TrackHighlightProps {
  tickHeight: number;
  dragStatus: DragStatus;
  duration: Seconds;
  trackWidth: number | undefined;
  setDragStatus?: SetState<DragStatus>;
  setNewComment?: SetState<UserComment | undefined>;
  userId?: UserId;
  callId?: FileUploadId;
}

export const TrackHighlight: React.FC<TrackHighlightProps> = ({
  dragStatus,
  duration,
  setDragStatus,
  setNewComment,
  tickHeight,
  trackWidth,
  userId,
  callId,
}) => {
  const [highlightRef, setHighlightRef] = useState<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [showShareNameDialog, setShowShareNameDialog] = useState(false);
  const [highlightName, setHighlightName] = useState(DEFAULT_SHARE);

  const {isDragging, startPos, endPos} = dragStatus;
  const showCommentPopper =
    !!userId &&
    !!setDragStatus &&
    !!setNewComment &&
    isDragging === false &&
    startPos !== null &&
    endPos !== null;

  const createShare = async () => {
    if (loading || !callId || !highlightName) return;
    try {
      setLoading(true);
      const {id} = await CommentSource.createHighlight(
        callId,
        buildComment(startPos, endPos, duration, trackWidth, userId).set('message', highlightName),
        duration
      );
      CallCardActions.getComments(callId).catch(console.error);
      CallCardActions.getUserCallActivity(callId).catch(console.error);
      SharingFormActions.updateSelectedHighlights([{value: id, label: highlightName}]);
      SharingFormActions.updateContentToShare({
        value: 'highlights',
        label: 'Selected highlights',
      });
      setShowShareNameDialog(false);
      setHighlightName(DEFAULT_SHARE);
      setDragStatus!({isDragging: false, startPos: null, endPos: null});
      SharingActions.showSharingDialog();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const createComment = useCallback(() => {
    if (!trackWidth) return console.error("Call section ref not defined, can't create comment");
    if (startPos === null || endPos === null) return;
    if (!setDragStatus || !setNewComment || !userId) return;

    try {
      setNewComment(buildComment(startPos, endPos, duration, trackWidth, userId));
      setDragStatus({isDragging: false, startPos: null, endPos: null});
    } catch (e) {
      console.error(e);
    }
  }, [setNewComment, trackWidth, duration, startPos, endPos, setDragStatus, userId]);

  const onClose = useCallback(() => {
    if (!showShareNameDialog) {
      setDragStatus!({isDragging: false, startPos: null, endPos: null});
    }
  }, [setDragStatus, showShareNameDialog]);

  if (!tickHeight || startPos === null || endPos === null) {
    return null;
  }

  return (
    <>
      <div
        ref={r => setHighlightRef(r)}
        className="track-highlight"
        style={{
          height: tickHeight,
          left: Math.min(startPos, endPos),
          width: Math.abs(startPos - endPos),
        }}>
        {!showShareNameDialog && setDragStatus && setNewComment && (
          <Menu
            getContentAnchorEl={null}
            anchorEl={highlightRef}
            open={showCommentPopper && !!highlightRef}
            anchorOrigin={{
              vertical: 30,
              horizontal: 'right',
            }}
            onClose={onClose}
            PaperProps={{onMouseLeave: onClose}}>
            {callId && startPos !== endPos && (
              <StyledMenuItem onClick={() => setShowShareNameDialog(true)}>
                <ShareIcon height={14} />
                Share selection
              </StyledMenuItem>
            )}
            <StyledMenuItem onClick={createComment}>
              <CommentIcon height={14} />
              Comment {startPos === endPos ? 'here' : 'on selection'}
            </StyledMenuItem>
          </Menu>
        )}
      </div>
      <Dialog open={showShareNameDialog} className="share-name-dialog">
        <div className="share-name-dialog-content">
          <h3>Enter a name for the highlight you want to share:</h3>
          <TextField
            fullWidth
            variant="outlined"
            onChange={e => setHighlightName(e.target.value)}
            value={highlightName}
          />
          <div className="actions">
            <Button
              onClick={() => {
                setShowShareNameDialog(false);
                setDragStatus!({isDragging: false, startPos: null, endPos: null});
              }}
              variant="outlined">
              Cancel
            </Button>
            <Button
              disabled={!highlightName}
              onClick={createShare}
              variant="contained"
              color="primary">
              {loading && <SpinnerCircular className="loading" size={14} />}
              Submit
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

function buildComment(startPos, endPos, duration, trackWidth, userId): UserComment {
  if (!trackWidth) {
    throw new Error("Call section ref not defined, can't create comment");
  }
  const startTime = (Math.min(startPos, endPos) / trackWidth) * duration;
  const endTime = (Math.max(startPos, endPos) / trackWidth) * duration;
  return new UserComment({
    startTime,
    endTime,
    userId,
    message: '',
  });
}

const StyledMenuItem = withStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: 10,
    color: theme.palette.primary.dark,
    fontWeight: 'bold',
    '& > .icon': {
      fill: theme.palette.primary.dark,
      marginTop: -3,
    },
  },
}))(MenuItem);
